/**
 * A nudge to connect an AI assistant, shown on Home until one is connected.
 *
 * **Self-retiring rather than one-shot.** The card asks `useMcpGrants` whether the
 * person already has an assistant and hides when they do, so it disappears by being
 * satisfied rather than by being dismissed and forgotten. That also means it comes back
 * if every assistant is later revoked, which is the moment the offer is relevant again.
 *
 * Dismissal is still there for someone who does not want one at all, because a nudge
 * that cannot be turned off is an advertisement. It is kept in this browser (localStorage) per
 * account, since the assistants it counts are the account's own; a preference nobody should
 * have to sync.
 *
 * Renders nothing while the query is in flight: appearing and then vanishing a moment
 * later, once the grants arrive, reads as a glitch.
 */
import { useState } from 'react';
import { Link } from 'react-router';
import { Bot } from 'lucide-react';
import { APP_NAME } from '@/branding';
import { Button } from '@/components/ui/button';
import { useMcpGrants } from '@/hooks/useMcpGrants';
import { accountScoped } from '@/lib/activeAccount';

const DISMISSED_KEY = 'connect-assistant-card-dismissed';

function readDismissed(): boolean {
  try {
    return localStorage.getItem(accountScoped(DISMISSED_KEY)) === '1';
  } catch {
    // Private mode and hardened settings can throw on access; showing the nudge is a
    // better failure than breaking the page.
    return false;
  }
}

export function ConnectAssistantCard() {
  const { data: grants, isLoading, isError } = useMcpGrants();
  const [dismissed, setDismissed] = useState(readDismissed);

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(accountScoped(DISMISSED_KEY), '1');
    } catch {
      // Not persisting the dismissal is a smaller problem than a thrown error.
    }
  };

  // Every reason not to show it, cheapest first. An error is a reason to stay quiet:
  // we cannot tell whether they already have one, and guessing wrong pesters someone
  // who is already connected.
  if (dismissed || isLoading || isError) return null;
  if ((grants?.length ?? 0) > 0) return null;

  return (
    <section
      className="bg-card border border-border rounded-lg p-5"
      aria-label="Connect an AI assistant"
    >
      <div className="flex items-start gap-3">
        <Bot className="size-5 shrink-0 text-muted-foreground mt-0.5" aria-hidden />
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold">Connect an AI assistant</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Let an assistant read your network and draft changes over MCP — scoped to one
            context, never beyond your own permissions, and revocable at any time. No API
            key to create or leak.
          </p>
          <div className="flex items-center gap-2 mt-3">
            <Button asChild size="sm">
              <Link to="/account/assistants">Connect to {APP_NAME}</Link>
            </Button>
            <Button size="sm" variant="ghost" onClick={dismiss}>
              Not now
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
